'use client';

import React from 'react';
import MarketIcon from 'src/components/Utils/MarketIcon';
import InlineAlert from 'src/components/primitives/InlineAlert/InlineAlert';
import SkeletonValuePlaceholder from 'src/components/SkeletonValuePlaceholder/SkeletonValuePlaceholder';
import { DRIFT_TOKEN_BANK_INDEX } from 'src/environmentVariables/EnvironmentVariables';
import { useSyncSpotMarketBalance } from 'src/hooks/useSyncWalletBalances';
import { findDaysHoursMinutesFromNow } from 'src/utils/dateTime';
import { useSpotMarketsLookup } from 'src/hooks/spotMarkets/useSpotMarketsLookup';
import StatDisplay from 'src/components/MarketStatsBar/StatDisplay';
import ValueWithLabelCard from 'src/components/primitives/ValueWithLabelCard/ValueWithLabelCard';
import Button from 'src/components/primitives/Button/Button';
import useStakingUtils from 'src/components/Staking/hooks/useStakingUtils';

export default function DriftStakingDashboardUserData() {
	const driftSpotMarket = useSpotMarketsLookup()[DRIFT_TOKEN_BANK_INDEX];

	useSyncSpotMarketBalance(driftSpotMarket);

	const {
		hasExistingUnstakeRequest,
		existingUnstakeRequestAmount,
		existingUnstakeRequestTimestamp,
		userStakeLoaded,
		userStakeAccountBalance,
		userStakeCostBasis,
		canWithdraw,
		handleCancelUnstakeRequest,
		handleWithdrawUnstakedAssets,
		isSubmitting,
		displayPrecision,
	} = useStakingUtils({
		isDriftStaking: true,
		defaultSelectedMarket: driftSpotMarket,
	});

	const unstakeDate = hasExistingUnstakeRequest
		? existingUnstakeRequestTimestamp
		: null;

	const unstakeDateFromNow = unstakeDate
		? findDaysHoursMinutesFromNow(unstakeDate)
		: '';

	const userEarnings = (() => {
		const earnings = userStakeAccountBalance - userStakeCostBasis;

		// Prevent negative zero display.
		// E.g. if earnings is -0.0001, it will display as 0.00.
		if (earnings < 0 && earnings > -(10 ** -displayPrecision)) {
			return 0;
		}

		return earnings;
	})();

	const userEarningsContent = !userStakeLoaded ? (
		<SkeletonValuePlaceholder loading className="h-4 w-18" />
	) : (
		parseFloat(userEarnings.toFixed(6))
	);

	const userStakeContent = !userStakeLoaded ? (
		<SkeletonValuePlaceholder loading className="h-4 w-18" />
	) : (
		userStakeAccountBalance
	);

	return (
		<div className="flex flex-col gap-y-lg">
			<div className="flex flex-col gap-y-lg sm:flex-row sm:justify-between">
				<div className="flex items-center gap-x-sm">
					<MarketIcon marketSymbol="DRIFT" sizeClass="size-8" />
					<span className="typo-t2 text-text-default">DRIFT</span>
				</div>
				<div className="hidden gap-x-4xl sm:flex">
					<StatDisplay
						header="Cumulative Earnings"
						className="text-text-default"
					>
						{userEarningsContent}
					</StatDisplay>
					<StatDisplay
						header="My Staked Amount"
						className="text-text-default"
						valueClassName="gap-x-sm"
					>
						{userStakeContent}
					</StatDisplay>
				</div>
				<ValueWithLabelCard.Container className="sm:hidden">
					<ValueWithLabelCard.Item label="Cumulative Earnings">
						{userEarningsContent}
					</ValueWithLabelCard.Item>
					<ValueWithLabelCard.Item label="My Staked Amount">
						{userStakeContent}
					</ValueWithLabelCard.Item>
				</ValueWithLabelCard.Container>
			</div>
			{hasExistingUnstakeRequest && (
				<div className="flex flex-col gap-lg sm:flex-row">
					{!canWithdraw && (
						<>
							<InlineAlert
								variant="info"
								title={`${existingUnstakeRequestAmount} DRIFT unstaking in ${unstakeDateFromNow}.`}
								className="flex-1"
							/>
							<Button
								size="md"
								variant="secondary"
								className="w-full sm:w-fit"
								onClick={handleCancelUnstakeRequest}
								isLoading={isSubmitting}
							>
								Cancel Unstake
							</Button>
						</>
					)}

					{canWithdraw && (
						<>
							<InlineAlert
								variant="success"
								className="flex-1"
								title={`${existingUnstakeRequestAmount} DRIFT is unstaked and ready to withdraw.`}
							/>
							<Button
								size="md"
								variant="secondary"
								className="w-full sm:w-fit"
								onClick={handleWithdrawUnstakedAssets}
								isLoading={isSubmitting}
							>
								Withdraw
							</Button>
						</>
					)}
				</div>
			)}
		</div>
	);
}
